import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { isLocaleCode, DEFAULT_LOCALE } from '@sshop/domain';
import { i18nInstance } from '@sshop/i18n';
import { useAuth } from '@sshop/auth';

import { routes } from '@/shared/lib/routes';

/**
 * Validates the `:locale` URL segment. Anything that isn't a supported locale
 * is redirected to the default locale, preserving the rest of the path.
 */
export function LocaleGuard({ children }: { children: ReactNode }) {
  const { locale } = useParams<{ locale: string }>();
  const location = useLocation();
  const valid = locale ? isLocaleCode(locale) : false;
  const queryClient = useQueryClient();
  const previousLocale = useRef(locale);

  useEffect(() => {
    if (valid && locale && i18nInstance.language !== locale) {
      void i18nInstance.changeLanguage(locale);
    }
    if (valid && previousLocale.current !== locale) {
      // API responses contain translated DB fields. Refresh both mounted and
      // cached queries so the old language cannot survive a URL switch.
      void queryClient.invalidateQueries({ refetchType: 'all' });
    }
    previousLocale.current = locale;
  }, [valid, locale, queryClient]);

  if (!valid) {
    const rest = location.pathname.split('/').slice(2).join('/');
    const search = location.search;
    return <Navigate to={`/${DEFAULT_LOCALE}/${rest}${search}`} replace />;
  }

  return <>{children}</>;
}

/**
 * Gate that requires an authenticated user. Unauthenticated visitors are
 * bounced to the home page (login flow is a feature concern; wire it here
 * when the auth feature exists).
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { session, status } = useAuth();
  const { locale } = useParams<{ locale: string }>();

  if (status === 'unknown') return null;
  if (!session) {
    const safeLocale = isLocaleCode(locale ?? '') ? (locale as 'ge' | 'en') : DEFAULT_LOCALE;
    return <Navigate to={routes.login(safeLocale)} replace />;
  }

  return <>{children}</>;
}

export function RequireCheckoutAuth({ children }: { children: ReactNode }) {
  const { session, status } = useAuth();
  const { locale } = useParams<{ locale: string }>();
  if (status === 'unknown') return null;
  if (!session) {
    const safeLocale = isLocaleCode(locale ?? '') ? (locale as 'ge' | 'en') : DEFAULT_LOCALE;
    return <Navigate to={routes.cart(safeLocale)} replace state={{ openCheckoutAuth: true }} />;
  }
  return <>{children}</>;
}

/**
 * Gate for guest-only routes (e.g. login, register). Authenticated users are
 * sent to their account dashboard.
 */
export function RequireGuest({ children }: { children: ReactNode }) {
  const { session, status } = useAuth();
  const { locale } = useParams<{ locale: string }>();

  if (status === 'unknown') return null;
  if (session) {
    const safeLocale = isLocaleCode(locale ?? '') ? (locale as 'ge' | 'en') : DEFAULT_LOCALE;
    return <Navigate to={routes.account(safeLocale)} replace />;
  }

  return <>{children}</>;
}
